import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";

type AlertType = "success" | "error" | "warning" | null;

interface AlertModalProps {
  show: boolean;
  type?: AlertType;
  title: string | React.ReactNode;
  message: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

// Fade-in background
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Scale-up modal
const popIn = keyframes`
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// Bounce for icons
const bounce = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease;
  z-index: 9999;

  body.dark-only & {
    background: rgba(0, 0, 0, 0.68);
  }
`;

const Modal = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 26px 24px;
  width: min(360px, calc(100vw - 32px));
  text-align: center;
  animation: ${popIn} 0.25s ease;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.18);

  body.dark-only & {
    background: #10101c;
    border-color: rgba(106, 113, 133, 0.28);
    box-shadow: 0 22px 55px rgba(0, 0, 0, 0.45);
  }
`;

const Title = styled.h2`
  margin: 14px 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;

  body.dark-only & {
    color: rgba(255, 255, 255, 0.92);
  }
`;

const Message = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: #4b5563;
  line-height: 1.5;
  overflow-wrap: break-word;
  word-wrap: break-word;
  white-space: normal;
  overflow: auto;
  max-height: 250px;

  body.dark-only & {
    color: rgba(255, 255, 255, 0.68);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 10px 22px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

  ${({ variant }) =>
    variant === "primary"
      ? `
        background: var(--theme-default, #2563eb);
        color: #fff;
        &:hover { filter: brightness(0.92); transform: translateY(-1px); }
        &:active { filter: brightness(0.98); transform: translateY(0); }
      `
      : `
        background: #f3f4f6;
        border-color: #e5e7eb;
        color: #111827;
        &:hover { background: #e5e7eb; transform: translateY(-1px); }
        &:active { background: #d1d5db; transform: translateY(0); }
      `}

  body.dark-only & {
    box-shadow: none;

    ${({ variant }) =>
      variant === "primary"
        ? `
          background: var(--theme-default, #2563eb);
          color: #ffffff;
        `
        : `
          background: #181824;
          border-color: rgba(106, 113, 133, 0.32);
          color: rgba(255, 255, 255, 0.82);
          &:hover { background: #202033; }
          &:active { background: #181824; }
        `}
  }
`;

const Icon = styled.div<{ type: AlertType }>`
  align-items: center;
  animation: ${bounce} 0.4s ease;
  border-radius: 999px;
  display: inline-flex;
  font-size: 34px;
  height: 66px;
  justify-content: center;
  margin-bottom: 8px;
  width: 66px;
  ${({ type }) =>
    type === "success" &&
    `
      background: #dcfce7;
      color: #16a34a;
    `}
  ${({ type }) =>
    type === "error" &&
    `
      background: #fee2e2;
      color: #dc2626;
    `}
  ${({ type }) =>
    type === "warning" &&
    `
      background: #fef3c7;
      color: #d97706;
    `}

  body.dark-only & {
    ${({ type }) =>
      type === "success" &&
      `
        background: rgba(34, 197, 94, 0.16);
        color: #4ade80;
      `}
    ${({ type }) =>
      type === "error" &&
      `
        background: rgba(239, 68, 68, 0.16);
        color: #f87171;
      `}
    ${({ type }) =>
      type === "warning" &&
      `
        background: rgba(250, 204, 21, 0.16);
        color: #facc15;
      `}
  }
`;

const AlertModal: React.FC<AlertModalProps> = ({
  show,
  type = "success",
  title,
  message,
  onConfirm,
  onCancel,
}) => {
  // Always call useEffect
  useEffect(() => {
    if (!show) return; // <-- safe early exit inside effect
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter") onConfirm();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [show, onConfirm]);

  if (!show) return null;

  const icon =
    type === "success"
      ? "\u2714"
      : type === "error"
      ? "\u2716"
      : type === "warning"
      ? "\u26A0"
      : null;

  const renderIcon = () => {
    switch (type) {
      case "success":
        return "✔";
      case "error":
        return "✖";
      case "warning":
        return "⚠";
      default:
        return null;
    }
  };

  return (
    <Backdrop>
      <Modal>
        <Icon type={type}>{icon}</Icon>
        <Title>{title}</Title>
        <Message>{message}</Message>
        <ButtonGroup>
          {onCancel && (
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="primary" onClick={onConfirm}>
            OK
          </Button>
        </ButtonGroup>
      </Modal>
    </Backdrop>
  );
};

export default AlertModal;
